import { Button, Flex, SimpleGrid } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { sortBy } from "lodash";
import { useState } from "react";

import { useCreatePengurus } from "../../../api/hooks/clikData/pengurus/useCreatePengurus.ts";
import { usePenguruses } from "../../../api/hooks/clikData/pengurus/usePenguruses.ts";
import { useUpdatePengurus } from "../../../api/hooks/clikData/pengurus/useUpdatePengurus.ts";
import { useUpdatePengurusStatus } from "../../../api/hooks/clikData/pengurus/useUpdatePengurusStatus.ts";
import MultiFieldCard from "../../../components/Cards/MultiFieldCard/MultiFieldCard.tsx";
import PengurusFormModal from "../../../components/ClikDataInfoForm/PengurusFormModal/PengurusFormModal.tsx";
import Plus from "../../../components/Icons/Plus.tsx";
import { Loader } from "../../../components/Loader.tsx";
import {
  ClikCity,
  ClikCountry,
  PengurusStatus,
  RolePermission,
} from "../../../graphql/generated.ts";
import { useMutationNotificationWrapper } from "../../../hooks/useMutationNotificationWrapper.tsx";
import { useCurrentUserContext } from "../../../providers/CurrentUserProvider.tsx";
import { RawPengurus } from "../../../types/clikData/pengurus.ts";
import { hasPermission } from "../../../utils/user.ts";
import { usePengurusesCardInfo } from "./hooks/usePengurusCardInfo.tsx";
import {
  formatPengurusToAddPengurusInput,
  formatPengurusToUpdatePengurusInput,
} from "./utils.ts";

interface PengurusInfoProps {
  clikDataParentId: number | null | undefined;
  loading: boolean;
  cities: ClikCity[] | null | undefined;
  countries: ClikCountry[] | null | undefined;
}

const PengurusInfo = ({
  loading,
  cities,
  countries,
  clikDataParentId,
}: PengurusInfoProps) => {
  const [selectedPengurus, setSelectedPengurus] = useState<{
    index: number;
    data: RawPengurus;
  } | null>(null);

  const [
    { data: pengurusesData, fetching: pengurusesFetching },
    refreshPenguruses,
  ] = usePenguruses({ clikDataParentId });

  const { user: currentUser } = useCurrentUserContext();

  const [, addPengurusToLimitRequest] = useMutationNotificationWrapper(
    useCreatePengurus(),
    {
      success: {
        message: "Pengurus has been added successfully.",
      },
    }
  );

  const [, updatePengurus] = useMutationNotificationWrapper(
    useUpdatePengurus(),
    {
      success: {
        message: "Pengurus has been updated successfully.",
      },
    }
  );

  const [, updatePengurusStatus] = useMutationNotificationWrapper(
    useUpdatePengurusStatus(),
    {
      success: {
        message: "Pengurus Status has been updated successfully.",
      },
    }
  );

  const hasEditAccess = hasPermission(
    currentUser,
    RolePermission.CanEditBuyerInfoPengurusInfo
  );

  const penguruses = sortBy(pengurusesData?.penguruses, "id");

  const [
    pengurusModalOpened,
    { open: pengurusModalOpen, close: pengurusModalClose },
  ] = useDisclosure(false);

  const handleStartEditPengurus = (value: RawPengurus, index: number) => {
    setSelectedPengurus({ index, data: value });
    pengurusModalOpen();
  };

  const handleChangeStatus = (id: number, value: PengurusStatus) => {
    updatePengurusStatus({ id, input: { status: value } }).then(() => {
      refreshPenguruses();
    });
  };

  const handleAddPengurus = (value: RawPengurus) => {
    if (!value) {
      return;
    }

    const input = formatPengurusToAddPengurusInput(
      Number(clikDataParentId),
      value
    );
    if (!input) {
      return;
    }

    addPengurusToLimitRequest({ input }).then(() => refreshPenguruses());
  };

  const handlePengurusModalClose = () => {
    setSelectedPengurus(null);
    pengurusModalClose();
  };

  const handleEditPengurus = (value: RawPengurus) => {
    if (!selectedPengurus || !value || !value.id) {
      return;
    }

    const input = formatPengurusToUpdatePengurusInput(value);

    if (!input) {
      return;
    }

    updatePengurus({ id: Number(value.id), input }).then(() =>
      refreshPenguruses()
    );
  };

  const handleSavePengurus = (value: RawPengurus) => {
    if (selectedPengurus) {
      handleEditPengurus(value);
    } else {
      handleAddPengurus(value);
    }
  };

  const pengurusesInfoCards = usePengurusesCardInfo({
    penguruses: penguruses,
    onEdit: handleStartEditPengurus,
    onStatusChange: handleChangeStatus,
  });

  return (
    <>
      <Flex direction="column" gap={20}>
        {hasEditAccess && (
          <Button
            leftIcon={<Plus />}
            variant="filledGolden"
            uppercase
            onClick={pengurusModalOpen}
          >
            add
          </Button>
        )}
        {pengurusesFetching && <Loader />}
        <SimpleGrid
          cols={1}
          verticalSpacing={16}
          breakpoints={[
            { minWidth: "sm", cols: 2, verticalSpacing: 20 },
            { minWidth: "lg", cols: 3, verticalSpacing: 20 },
          ]}
          spacing={20}
        >
          {pengurusesInfoCards?.map((item, index) => (
            <MultiFieldCard
              key={index}
              {...item}
              loading={loading || pengurusesFetching}
            />
          ))}
        </SimpleGrid>
      </Flex>
      {pengurusModalOpened && (
        <PengurusFormModal
          value={selectedPengurus?.data ?? null}
          disabledFields={selectedPengurus && ["name"]}
          cities={cities}
          countries={countries}
          opened={true}
          onClose={handlePengurusModalClose}
          onSubmit={handleSavePengurus}
        />
      )}
    </>
  );
};

export default PengurusInfo;
